import { useEffect, useLayoutEffect, useRef } from "react";
import { subscribeTabSync } from "../lib/tabSync";
import { dmPeerIdFromConversation } from "./state/chatState";
import type { FrameRouterDeps } from "./realtime/frameRouter";
import { useAuth } from "./context/AuthContext";
import { useContacts } from "./context/ContactsContext";
import { useChat } from "./context/ChatContext";
import { useCall } from "./context/CallContext";
import { useRealtime } from "./context/RealtimeContext";

/** Registers frame-router deps and tab sync; must mount under CallProvider. */
export function StyxInfrastructureBinder() {
  const auth = useAuth();
  const contacts = useContacts();
  const chat = useChat();
  const call = useCall();
  const rt = useRealtime();

  const authRef = useRef(auth);
  authRef.current = auth;
  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const callRef = useRef(call);
  callRef.current = call;
  const rtRef = useRef(rt);
  rtRef.current = rt;

  useLayoutEffect(() => {
    const deps: FrameRouterDeps = {
      getKeys: () => authRef.current.keysRef.current,
      getIdentityRow: () => authRef.current.identityRowRef.current,
      getSelectedConversation: () => chatRef.current.selectedConversationRef.current,
      getIncoming: () => contactsRef.current.contactsState.incoming,
      isWsLeader: () => rtRef.current.isWsLeaderRef.current,
      voiceSessionRef: callRef.current.voiceSessionRef,
      sendRealtimeCommand: (type, payload, id) => rtRef.current.sendRealtimeCommand(type, payload, id),
      setBoot: (key, state, detail) => authRef.current.setBoot(key, state, detail),
      dispatchChat: (action) => chatRef.current.dispatchChat(action),
      dispatchContacts: (action) => contactsRef.current.dispatchContacts(action),
      dispatchRelay: (action) => rtRef.current.dispatchRelay(action),
      setMyAvatarUrls: (urls) => authRef.current.setMyAvatarUrls(urls),
      refreshContacts: () => contactsRef.current.refreshContacts(),
      refreshUnreadCounts: () => contactsRef.current.refreshUnreadCounts(),
      notifyContactsChanged: () => contactsRef.current.notifyContactsChanged(),
      notifyMessagesChanged: (peerId) => chatRef.current.notifyMessagesChanged(peerId),
      repairSync: () => chatRef.current.repairSync(),
      hideBoot: () => authRef.current.hideBoot(),
    };
    rt.registerFrameDeps(deps);
    return () => rt.registerFrameDeps(null);
  }, [rt.registerFrameDeps]);

  useEffect(() => {
    if (!auth.sodiumReady) return;
    return subscribeTabSync((msg) => {
      const r = rtRef.current;
      const c = contactsRef.current;
      const ch = chatRef.current;
      const a = authRef.current;

      if (msg.type === "contacts-changed" || (msg.type === "idb-changed" && msg.scope === "contacts")) {
        void c.refreshContacts();
        void c.refreshUnreadCounts();
        return;
      }
      if (msg.type === "messages-changed" || (msg.type === "idb-changed" && msg.scope === "messages")) {
        void (async () => {
          if (dmPeerIdFromConversation(ch.selectedConversationRef.current) === msg.peerId) {
            await ch.loadPeerMessagesIntoState(msg.peerId);
          } else {
            await c.refreshUnreadCounts();
          }
        })();
        return;
      }
      if (msg.type === "ws-command") {
        if (!r.isWsLeader) return;
        r.clientRef.current?.send(msg.frameType, msg.payload, msg.id);
        return;
      }
      if (msg.type === "ws-frame") {
        if (r.isWsLeader) return;
        r.routeIncomingFrame(msg.frame);
        return;
      }
      if (msg.type === "ws-state") {
        if (r.isWsLeader) return;
        r.dispatchRelay({ type: "set-conn", conn: msg.conn });
        r.dispatchRelay({ type: "set-authenticated", authenticated: msg.authenticated });
        if (msg.conn === "open") a.setBoot("relay", "done", "browser leader connected");
        if (msg.authenticated) a.setBoot("auth", "done", "browser leader authenticated");
      }
    });
  }, [auth.sodiumReady]);

  return null;
}
